import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DatabaseZap, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import type { BadgeVariant } from '@/types/common.types';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { LEAD_IMPORT_MODULE_KEY } from '@/lib/leadImport';
import {
  listImportRuns,
  IMPORT_STATUS_LABELS,
  isImportRunActive,
  type ImportRunStatus,
  type ImportRunSummary,
} from '@/services/import.service';

/**
 * Landing page for the admin Import tools: the available source systems, and
 * the history of runs.
 *
 * Two sources: leads from a spreadsheet, and Zenoti. The spreadsheet card shows
 * only when the account has the opt-in Lead Import module, matching the menu
 * item and route guard for that page. Adding another source means a card here
 * plus its own wizard route — the run lifecycle, staging and reporting are
 * source agnostic.
 */

interface SourceDefinition {
  key: string;
  name: string;
  description: string;
  href: string;
  available: boolean;
}

const SOURCES: SourceDefinition[] = [
  {
    key: 'leads-csv',
    name: 'Leads from a spreadsheet',
    description:
      "Upload a spreadsheet of leads, match up its columns, and add them to this practice.",
    href: '/admin/import/leads',
    available: true,
  },
  {
    key: 'zenoti',
    name: 'Zenoti',
    description:
      "Pull a practice's guests, providers, services, appointments and sales history from Zenoti's API.",
    href: '/admin/import/zenoti',
    available: true,
  },
];

function statusVariant(status: ImportRunStatus): BadgeVariant {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'preview_ready') return 'warning';
  return 'neutral';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

export function ImportAdmin() {
  const navigate = useNavigate();
  const { explicit: leadImportOn } = useModuleAccess(LEAD_IMPORT_MODULE_KEY);
  const sources = SOURCES.filter((source) => source.key !== 'leads-csv' || leadImportOn);

  const { data: runs, isLoading, error } = useQuery({
    queryKey: ['admin-import-runs'],
    queryFn: listImportRuns,
    // A run in flight advances on the worker, so refresh while any is active.
    refetchInterval: (query) => {
      const rows = query.state.data as ImportRunSummary[] | undefined;
      return rows?.some((run) => isImportRunActive(run.status)) ? 5000 : false;
    },
  });

  return (
    // p-6 encloses PageHeader, matching AccessRestrictionsAdmin. The shell adds
    // no padding (Layout's <main> is bare) and PageHeader carries only mb-6, so
    // without it the title sits at the content origin and is painted over by the
    // sidebar's collapse chevron (absolute, z-30, overlapping x=0-12px).
    <div className="p-6 space-y-6">
      <PageHeader title="Import" />

      {isLoading && <SpinnerOverlay />}

      {error && (
        <Card>
          <p className="text-sm text-danger">{getErrorMessage(error)}</p>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sources.map((source) => (
          <Card key={source.key} className="flex flex-col justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <DatabaseZap className="h-5 w-5 text-slate-500" />
                <span className="font-semibold text-slate-900">{source.name}</span>
              </div>
              <p className="text-sm text-slate-600">{source.description}</p>
            </div>
            <div className="mt-4">
              <Button
                onClick={() => navigate(source.href)}
                disabled={!source.available}
                className="w-full"
              >
                Start an import
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card noPadding>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Run</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Destination account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(runs ?? []).map((run) => (
                <tr key={run.importRunId} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">#{run.importRunId}</td>
                  <td className="px-4 py-3 capitalize">{run.source}</td>
                  <td className="px-4 py-3">
                    {run.accountName || `Account ${run.accountId}`}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(run.status)}>
                      {IMPORT_STATUS_LABELS[run.status] ?? run.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{run.progress || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(run.startedAt ?? run.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/import/zenoti?runId=${run.importRunId}`)}
                    >
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
              {!isLoading && (runs ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No imports have been run yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
